import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getProducts } from '../lib/api';

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await getProducts();
        setProducts(data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const categories = [
    { id: 'all', name: 'Tous' },
    { id: 'nettoyage', name: 'Nettoyage' },
    { id: 'sante-confort', name: 'Santé & Confort' }
  ];

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const stagger = {
    visible: { transition: { staggerChildren: 0.1 } }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-ghrb-orange border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="products-page" className="min-h-screen bg-white">
      {/* Header */}
      <section className="py-16 lg:py-24 bg-ghrb-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-2xl"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <p className="font-outfit text-xs uppercase tracking-[0.2em] text-ghrb-orange mb-4">
              Nos produits
            </p>
            <h1 className="font-outfit text-4xl lg:text-5xl font-bold tracking-tight text-ghrb-black mb-6">
              La gamme GHRB
            </h1>
            <p className="font-jakarta text-lg text-ghrb-gray-dark">
              Des produits sélectionnés avec soin pour leur efficacité et leur qualité. 
              Chaque article répond à un besoin réel du quotidien.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 border-b border-ghrb-black/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-2.5 rounded-full font-jakarta text-sm font-medium transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-ghrb-black text-white'
                    : 'bg-ghrb-gray text-ghrb-black hover:bg-ghrb-black/10'
                }`}
                data-testid={`category-filter-${category.id}`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12"
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            {filteredProducts.map((product) => (
              <motion.div key={product.id} variants={fadeInUp}>
                <Link 
                  to={product.is_coming_soon ? '#' : `/produits/${product.slug}`}
                  className={`block group ${product.is_coming_soon ? 'cursor-default' : ''}`}
                  data-testid={`product-card-${product.slug}`}
                >
                  <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-ghrb-gray mb-6">
                    <img
                      src={product.images?.[0]}
                      alt={product.name}
                      className={`w-full h-full object-cover ${!product.is_coming_soon ? 'group-hover:scale-105 transition-transform duration-500' : 'opacity-60'}`}
                    />
                    {product.is_coming_soon && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="coming-soon-badge text-white font-jakarta text-sm font-semibold px-6 py-2 rounded-full">
                          Bientôt disponible
                        </span>
                      </div>
                    )}
                    {!product.is_coming_soon && product.original_price && (
                      <div className="absolute top-4 left-4">
                        <span className="bg-ghrb-orange text-white font-jakarta text-xs font-bold px-3 py-1.5 rounded-full">
                          Promo
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="font-outfit text-xs uppercase tracking-[0.15em] text-ghrb-gray-dark mb-2 block">
                        {product.category === 'nettoyage' ? 'Nettoyage' : 'Santé & Confort'}
                      </span>
                      <h3 className="font-outfit text-xl font-semibold text-ghrb-black mb-2">
                        {product.name}
                      </h3>
                      <p className="font-jakarta text-ghrb-gray-dark text-sm line-clamp-2">
                        {product.short_description}
                      </p>
                    </div>
                    {!product.is_coming_soon && (
                      <div className="text-right flex-shrink-0">
                        <p className="font-outfit text-lg font-bold text-ghrb-black">
                          {product.price?.toFixed(2)}€
                        </p>
                        {product.original_price && (
                          <p className="font-jakarta text-sm text-ghrb-gray-dark line-through">
                            {product.original_price?.toFixed(2)}€
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-16">
              <p className="font-jakarta text-ghrb-gray-dark">
                Aucun produit dans cette catégorie pour le moment.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
